import * as React from 'react'
import { FunctionComponent, useState } from 'react'
import OnboardingModal from '../onboarding-modal/onboarding-modal'
import OnboardingTutorialContent from '../onboarding-tutorial-content'

interface onboardingProps {
  cardDescription: string
  cardTitle: string
  image: string
  schedulerVersion?: boolean
}
const OnboardingCard: FunctionComponent<onboardingProps> = ({
  cardDescription,
  cardTitle,
  image,
  schedulerVersion
}) => {
  const [showModal, setShowModal] = useState(false)
  const onClose = () => {
    setShowModal(false)
  }
  function openTutorial() {
    setShowModal(true)
  }
  const tutorialSections = {
    designingTutorialSection: [
      {
        title: $localize`:onboarding.description|Onboarding Description - Key Tips 1@@onboardingKeyTips1:Follow the recommended session order`,
        text: $localize`:onboarding.description|Onboarding Description - Key Tips 1 Desc@@onboardingKeyTips1Desc:Avoid the temptation to cut sessions or combine weeks. We’re so sure you’ll find success that we’ve set up the session order for you —it’s designed to build week by week toward deeper connection and impact.`
      },
      {
        title: $localize`:onboarding.description|Onboarding Description - Key Tips 2@@onboardingKeyTips2:Customise your course calendar accurately`,
        text: $localize`:onboarding.description|Onboarding Description - Key Tips 2 Desc@@onboardingKeyTips2Desc:— Even if giving talks live — as your schedule syncs seamlessly with other features in MyAlpha, providing you with helpful resources when you need it!`
      },
      {
        title: $localize`:onboarding.description|Onboarding Description - Key Tips 3 Desc@@onboardingKeyTip3:Set aside time to train your team before the course begins`,
        text: $localize`:onboarding.description|Onboarding Description - Key Tips 3 Desc@@onboardingKeyTips3Desc:This is essential to creating a safe and welcoming space for your guests. We recommend gathering 2-3 weeks before your Alpha course begins.`
      }
    ],
    setupTutorialSection: [
      {
        title: $localize`:onboarding.tutorialTitle|Onboarding Tutorial Title - Tutorial section title@@onboardingTutorialSection1Title:Can I skip a week if we have a holiday or church event?`,
        text: $localize`:onboarding.tutorialText1|Onboarding Tutorial Text - Tutorial section text@@onboardingTutorialSection1Text:Yes! Just edit the session dates as needed—each session’s date is independent.`
      },
      {
        title: $localize`:onboarding.tutorialTitle2|Onboarding Tutorial Title - Tutorial section title2@@onboardingTutorialSection2Title:Can I skip an episode if we are not able to run the full course?`,
        text: $localize`:onboarding.tutorialText2|Onboarding Tutorial Text - Tutorial section text2@@onboardingTutorialSection2Text:Yes! You can turn a session off using the <b>Toggle</b> to skip that episode. The session will turn inactive but still accessible to you if you change your mind.`
      },
      {
        title: $localize`:onboarding.tutorialTitle3|Onboarding Tutorial Title - Tutorial section title3@@onboardingTutorialSection3:I accidentally turned off a session. How do I get it back?`,
        text: $localize`:onboarding.tutorialText3|Onboarding Tutorial Text - Tutorial section text3@@onboardingTutorialSection3Text:No worries! Just click on the <b>Toggle</b> button and turn it back on.`
      },
      {
        title: $localize`:onboarding.tutorialTitle4|Onboarding Tutorial Title - Tutorial section title4@@onboardingTutorialSection4:What happens if I change the date of one session?`,
        text: $localize`:onboarding.tutorialText4|Onboarding Tutorial Text - Tutorial section text4@@onboardingTutorialSection4Text:Only that session’s date will change—your other sessions will stay as they are.`
      },
      {
        title: $localize`:onboarding.tutorialTitle5|Onboarding Tutorial Title - Tutorial section title5@@onboardingTutorialSection5:Can I move the Holy Spirit sessions to different days?`,
        text: $localize`:onboarding.tutorialText5|Onboarding Tutorial Text - Tutorial section text5@@onboardingTutorialSection5Text:Yes! While we recommend running them together, you can schedule them however works best for your group.`
      }
    ]
  }

  const modal1overview = {
    part1: $localize`:onboarding.description|Onboarding Description - Overview Section1 Part1@@onboardingOverviewSection1Part1:One of the ways to ensure a great Alpha course is to utilize the Alpha Talks. Whether you are presenting the content using a video series or delivering live using the scripts, the Alpha Talks present the Gospel in a way that connects with the heart and the head while sparking meaningful discussion and moments for prayer ministry. Every Alpha Talk explores a different aspect of the Christian faith, and they've been designed to build on one another in order, especially before the Alpha weekend/day away. One guest explained it like another piece of the puzzle falling into place each week.`,
    part2: $localize`:onboarding.description|Onboarding Description - Overview Section1 Part2@@onboardingOverviewSection1Part2:We recommend running Alpha over 10-2 consecutive weeks (or 8 weeks for Alpha Youth) to give guests time to build trust, process what they’re learning, and feel safe to share openly. Sticking to the full schedule helps create the kind of environment where meaningful conversations and real relationships can grow.`
  }
  const modal2overview = {
    part1: $localize`:onboarding.setup|set up Description - setup Overview Section 2@@onboardingSetupOverviewSection2:Welcome to you Alpha Schedule.
    Planning your Alpha course just got easier! 
    This flexible scheduling tool helps you customize your course calendar to your needs, access weekly resources in one place, 
    and set up your schedule with best practices recommendations. 
    Let me show you what you can do.`
  }
  return (
    <>
      <div
        className='relative border w-full rounded-lg flex p-6 bg-[#24201E] bg-contain bg-no-repeat bg-right box-border'
        style={{
          backgroundImage: image ? `url(${image})` : ''
        }}
      >
        <div className='flex flex-col gap-4 md:gap-6 lg:gap-8 justify-between'>
          <div className='flex flex-col justify-stretch gap-1'>
            <h6 className='text-alpha-oatmeal font-medium font-body text-xs m-0'>
              {cardTitle}
            </h6>
            <h3 className='text-alpha-oatmeal leading-[1.3] text-xl lg:text-2xl font-alpha-family m-0'>
              {cardDescription}
            </h3>
          </div>
          <button
            className='py-2 px-4 whitespace-nowrap w-fit rounded-md bg-transparent text-alpha-oatmeal font-medium border border-white '
            onClick={() => setShowModal(true)}
          >
            {$localize`:onboardingCard.button|Learn more Buttonn@@learnMoreButton:Learn More`}
          </button>
        </div>
      </div>
      {/* onboarding 1 */}
      {showModal && !schedulerVersion && (
        <OnboardingModal
          openDialog={showModal}
          showClose={true}
          onClose={onClose}
        >
          <OnboardingTutorialContent
            heading={$localize`:onboarding.modal|Onboarding Modal - Modal1 Heading@@onboardingModal1Heading:Schedule  •  Onboarding Tutorial 1`}
            title={$localize`:onboarding.title|Onboarding title - Card1 Title@@onboardingCard1Title:Designing a Successful Alpha Course`}
            overview={modal1overview}
            coverPhoto='../images/cards/onboarding_card1.png'
            sectionTitle={$localize`:onboarding.description|Onboarding Description - Card1 Section title@@onboardingCard1SectionTitle:Key Tips to Keep in Mind`}
            tutorialSectionContent={tutorialSections.designingTutorialSection}
            endSection={$localize`:onboarding.description|Onboarding Description - End Section 1@@onboardingEndSection1:When you take the time to plan your schedule thoughtfully and prepare your team well, you’re setting the stage for a powerful Alpha experience—one where guests feel welcomed, supported, and open to exploring life, faith, and meaning.`}
          ></OnboardingTutorialContent>
        </OnboardingModal>
      )}
      {/* onboarding 2 */}
      {showModal && schedulerVersion && (
        <OnboardingModal
          openDialog={showModal}
          showClose={true}
          onClose={onClose}
        >
          <OnboardingTutorialContent
            heading={$localize`:onboarding.heading|Onboarding heading - Card2 Section heading@@onboardingCard2SectionHeading:Schedule  •  Onboarding Tutorial 2`}
            title={$localize`:onboarding.description|Onboarding Description - Card2 Title@@onboardingCard2Title:How to Set Up My Alpha Schedule?`}
            overview={modal2overview}
            coverPhoto='../images/cards/onboarding_card2.png'
            sectionTitle={$localize`:onboarding.sectionTitle|Onboarding section - Card2 section title@@onboardingCard2SectionTitle:Frequently Asked Questions`}
            tutorialSectionContent={tutorialSections.setupTutorialSection}
            endSection={$localize`:onboarding.description|Onboarding Description - End Section 2@@onboardingEndSection2:Need more help? Reach out to us. We are here to help! Enjoy running your Alpha! 🎉`}
            isSetupTutorial={true}
          ></OnboardingTutorialContent>
        </OnboardingModal>
      )}
    </>
  )
}

export default OnboardingCard
